import { useClickOutside } from '@common/hooks';
import { MacroKeywords, MacroListingModel } from '@common/models';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Overlay } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { useSlate } from 'slate-react';
import { RichTextDisplay } from './RichTextDisplay';
import { RichTextBoxEditor } from './richTextEditorTypes';
import { useMacroContext } from './useMacros';
import { MacroTargetType } from '@common/models/Enums/MacroType';

export const MacroDropdown = observer(
  (props: {
    element: HTMLSpanElement | null;
    searchText: string | null;
    wrapperRef: React.RefObject<HTMLDivElement>;
    macroTargetType?: MacroTargetType;
    isTrasientKeywords: boolean;
  }) => {
    const editor = useSlate() as RichTextBoxEditor;
    const macroContext = useMacroContext() ?? {
      macros: [],
      isLoading: false,
      error: null,
      setSearchText: () => {},
      setTransientKeywords: () => {},
      macroTrasientKeywords: null,
      macroKeywords: null,
    };

    const { macros, isLoading, error, setSearchText } = macroContext;
    const [macroTarget] = useState<MacroTargetType | undefined>(
      props.macroTargetType,
    );
    const [previewMacro, setPreviewMacro] = useState<MacroListingModel | null>(
      null,
    );
    const overlayRef = useRef<HTMLElement>(null);

    useClickOutside(overlayRef, (event: PointerEvent) => {
      const target = event.target as ChildNode;
      if (target.nodeType === Node.ELEMENT_NODE) {
        //close the macro dropdown if the outside target element is not the macro search text element
        if (props.element !== target && !props.element?.contains(target)) {
          setPreviewMacro(null);
          editor.closeMacros();
          return;
        }
      }
    });

    const clickPreview = (macro: MacroListingModel) => {
      setPreviewMacro(macro);
    };

    const processMacroPlaceholders = (text: string) => {
      macroContext.setTransientKeywords(props.isTrasientKeywords);
      const macroKeywordsType = macroContext.macroKeywords;
      const html = macroKeywordsType
        ? MacroKeywords.processTextForMacros(text, macroKeywordsType)
        : text;
      return html;
    };

    const insertMacro = (macro: MacroListingModel) => {
      const html = processMacroPlaceholders(macro.content);
      if (html) editor.insertMacro(html);
      else editor.closeMacros();
    };

    const closeModal = useCallback(() => {
      setPreviewMacro(null);
      RichTextBoxEditor.focus(editor);
    }, [editor]);

    useEffect(() => {
      if (!props.searchText || !props.searchText.startsWith('/')) {
        setSearchText(undefined);
      } else {
        let text = _.trimStart(props.searchText.trim(), '/');
        if (!text) setSearchText(undefined);
        else setSearchText(text);
      }
    }, [props.searchText, setSearchText]);

    const isDom = typeof document === 'object';
    if (!isDom) return null;

    return (
      <>
        {!previewMacro && props.element && (
          <Overlay
            ref={overlayRef}
            show={!previewMacro}
            container={props.wrapperRef}
            target={props.element}
            placement="bottom-start"
            offset={[0, 5]}
            rootClose={false}
          >
            {({ placement, arrowProps, show: _show, popper, ...props }) => (
              <div
                className={classNames({
                  'macroCard card': true,
                  hide: !_show,
                })}
                {...props}
              >
                <ul className="list-group list-group-flush richtextbox macroDropDownList">
                  {isLoading &&
                    !error &&
                    _.times(10, (i) => (
                      <li
                        key={i}
                        className={classNames({
                          disabled: isLoading || Boolean(error),
                          'macroItem list-group-item list-group-item-action':
                            true,
                        })}
                      >
                        <Skeleton
                          className="loader"
                          count={1}
                          width={100}
                          height={10}
                        />
                        <Skeleton
                          className="loader"
                          count={1}
                          width={100}
                          height={10}
                        />
                        <Skeleton
                          className="loader"
                          count={1}
                          width={100}
                          height={10}
                        />
                      </li>
                    ))}
                  {!isLoading &&
                    !error &&
                    macros.length > 0 && [
                      macros
                        .filter((m) => m['macroTargetTypeId'] == macroTarget)
                        .map((m, i) => (
                          <li
                            className="macroItem list-group-item list-group-item-action"
                            key={i}
                          >
                            <span className="name">{m.name}</span>
                            <span className="createdBy"> - {m.createdBy}</span>
                            <span className="actions">
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => clickPreview(m)}
                              >
                                Preview
                              </button>
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => insertMacro(m)}
                              >
                                Insert
                              </button>
                            </span>
                          </li>
                        )),
                    ]}
                  {!isLoading && !error && macros.length === 0 && (
                    <li
                      key="noresults"
                      className="macroItem list-group-item list-group-item-action disabled"
                    >
                      <span className="text-warning">
                        No matching macrors found!
                      </span>
                    </li>
                  )}
                  {error && (
                    <li
                      key="macro-error"
                      className={classNames({
                        disabled: Boolean(error),
                        'macroItem list-group-item list-group-item-action':
                          true,
                      })}
                    >
                      <span className="error text-danger">
                        There was an error when loading your macros. Try
                        refreshing your browser page.
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </Overlay>
        )}
        {previewMacro !== null && (
          <Modal
            size="xl"
            backdrop="static"
            show={previewMacro !== null}
            centered
            className="macroPreviewModal"
            onHide={closeModal}
          >
            <Modal.Header closeButton>
              <Modal.Title>{previewMacro.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <RichTextDisplay
                value={processMacroPlaceholders(previewMacro.content)}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button
                aria-label="cancel"
                variant="light"
                className="cancel"
                onClick={closeModal}
              >
                Cancel
              </Button>
              <Button
                aria-label="submit"
                variant="primary"
                className="confirm"
                onClick={() => insertMacro(previewMacro)}
              >
                ADD
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </>
    );
  },
);
